import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Chart as ChartJS, ArcElement, Legend } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import styles from 'modules/Finance/Wallets/subcomponents/Chart/Chart.module.scss'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(ArcElement, Legend)

const RoundedDoughnutPlugin = {
  afterUpdate: function (chart) {
    const arcs = chart.getDatasetMeta(0).data

    arcs.forEach(function (arc) {
      arc.round = {
        x: (chart.chartArea.left + chart.chartArea.right) / 2,
        y: (chart.chartArea.top + chart.chartArea.bottom) / 2,
        radius: (arc.outerRadius + arc.innerRadius) / 2,
        thickness: (arc.outerRadius - arc.innerRadius) / 2,
        backgroundColor: arc.options.backgroundColor,
      }
    })
  },
  afterDraw: (chart) => {
    const { ctx } = chart

    chart.getDatasetMeta(0).data.forEach((arc) => {
      const endAngle = Math.PI / 2 - arc.endAngle

      ctx.save()
      ctx.translate(arc.round.x, arc.round.y)
      ctx.fillStyle = arc.options.backgroundColor
      ctx.beginPath()
      ctx.arc(
        arc.round.radius * Math.sin(endAngle),
        arc.round.radius * Math.cos(endAngle),
        arc.round.thickness,
        0,
        2 * Math.PI,
      )
      ctx.closePath()
      ctx.fill()
      ctx.restore()
    })
  },
}

export const DoughnutChart = ({ colors, data }) => {
  const total = data.reduce((a, v) => a + v, 0)
  const inPercent = data.map((v) => (v ? Math.max((v / total) * 100, 1) : 0))

  const isZeroData = useMemo(
    () => data.length === 0 || data.filter((value) => value === 0).length === data.length,
    [data],
  )

  const stateData = useMemo(
    () => ({
      datasets: [
        {
          data: inPercent,
          backgroundColor: colors,
          hoverBorderWidth: 0,
          borderAlign: 'inner',

          borderRadius: 0,
          borderWidth: 0,
          datalabels: {
            align: 'end',
            anchor: 'end',
          },
        },
      ],
    }),
    [data, colors],
  )

  const options = useMemo(
    () => ({
      responsive: true,
      cutoutPercentage: 100,

      cutout: '80%',

      layout: {
        padding: 28,
      },

      plugins: {
        legend: false,
        title: {
          display: false,
        },
        tooltip: false,

        datalabels: {
          color: '#B6BECD',
          font: {
            size: 12,
            lineHeight: 1.3,
            weight: 'normal',
          },
          padding: 0,
          formatter: function (value, context) {
            const total = context.dataset.data.reduce((acc, d) => acc + d, 0)

            return Math.round((value * 100) / total) + '%'
          },
        },
      },
    }),
    [],
  )

  return (
    <div
      className={clsx(styles['chart__doughnut'], {
        [styles['chart__doughnut_zero-data']]: isZeroData,
      })}
    >
      <Doughnut
        type="doughnut"
        data={stateData}
        options={options}
        plugins={[ChartDataLabels, RoundedDoughnutPlugin]}
      />
    </div>
  )
}

DoughnutChart.propTypes = {
  colors: PropTypes.array,
  data: PropTypes.array,
  currency: PropTypes.string,
}
